/**
 * Auto-hiding player chrome, owned imperatively.
 *
 * Deliberately NOT React state with a keydown listener, which is what this
 * was. Two reasons, and the second is the one that actually broke on the TV:
 *
 *  1. A layer that decides "this press only woke the chrome, so don't also act
 *     on it" has to know whether the chrome was hidden AT THE INSTANT the key
 *     arrived. React state is a render behind, so it would answer with the
 *     value from before the press it is trying to react to.
 *
 *  2. The Fire TV wrapper consumes D-pad keys in Kotlin and calls the page
 *     directly, so no DOM keydown exists to reset a timer with. On the one
 *     device the auto-hide is for, the remote could not wake the bar at all —
 *     which reads exactly like "Enter does nothing".
 *
 * Same shape as lib/player.ts: an imperative module, read through
 * useSyncExternalStore. See docs/ARCHITECTURE.md §9.
 */

const HIDE_AFTER_MS = 4200;

let hidden = false;
let armed = false;
let timer: ReturnType<typeof setTimeout> | null = null;
const subscribers = new Set<() => void>();

function emit(): void {
  for (const s of subscribers) s();
}

function restart(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  if (!armed) return;
  timer = setTimeout(() => {
    if (!armed || hidden) return;
    hidden = true;
    emit();
  }, HIDE_AFTER_MS);
}

/** Show the chrome and restart the countdown. Returns whether it was hidden. */
export function wakeChrome(): boolean {
  const was = hidden;
  if (hidden) { hidden = false; emit(); }
  restart();
  return was;
}

/** Only over video does chrome hide at all; on the grid it is the UI. */
export function armChrome(on: boolean): void {
  armed = on;
  if (!on && hidden) { hidden = false; emit(); }
  restart();
}

/** Hide immediately — used when focus leaves the chrome deliberately. */
export function hideChrome(): void {
  if (!armed || hidden) return;
  hidden = true;
  if (timer) { clearTimeout(timer); timer = null; }
  emit();
}

export function isChromeHidden(): boolean { return hidden; }

export function subscribeChrome(fn: () => void): () => void {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
}
